import { Box, Paper, useTheme } from '@mui/material';
import { Modal } from '@mui/material';
import { Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Button, Icon } from '@verantos-dev/shared-components';

import { useAppDispatch } from '../../../../lib/store';
import { useFinalizeAnnotationMutation } from '../../api/annotationApiSlice';
import { clearConceptAnnotationIdsInEditMode } from '../../state/annotationSlice';

interface FinalizedAnnotationModalProps {
  showFinalizedAnnotationModal: boolean;
  setShowFinalizedAnnotationModal: (showFinalizedAnnotationModal: boolean) => void;
}

export const FinalizedAnnotationModal = ({
  showFinalizedAnnotationModal,
  setShowFinalizedAnnotationModal,
}: FinalizedAnnotationModalProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { taskId } = useParams();
  const [finalizeAnnotation, { isLoading: isFinalizing }] = useFinalizeAnnotationMutation();
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const handleFinalize = async () => {
    if (!taskId) {
      return;
    }

    try {
      setError(null);
      await finalizeAnnotation({ taskId }).unwrap();
      // Close modal on success
      setShowFinalizedAnnotationModal(false);
      dispatch(clearConceptAnnotationIdsInEditMode());
    } catch (err) {
      console.error('Failed to finalize annotation:', err);
      setError('Failed to finalize annotations. Please try again.');
    }
  };
  return (
    <Modal
      open={showFinalizedAnnotationModal}
      onClose={() => setShowFinalizedAnnotationModal(false)}
      aria-labelledby="finalized-annotation-modal-title"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          p: theme.spacing(6),
          outline: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography id="finalized-annotation-modal-title" variant="h6">
              {formatMessage({ id: 'annotationContent.finalizedAnnotationModal.title' })}
            </Typography>
            <IconButton onClick={() => setShowFinalizedAnnotationModal(false)} size="small">
              <Icon id="finalized-annotation-modal-close-icon" icon="clear" width={16} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              {formatMessage({ id: 'annotationContent.finalizedAnnotationModal.description' })}
            </Typography>
            {error && (
              <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                {error}
              </Typography>
            )}
            {isFinalizing && (
              <Typography variant="body1">
                {formatMessage({
                  id: 'annotationContent.finalizedAnnotationModal.finalizingMessage',
                })}
              </Typography>
            )}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: theme.spacing(3),
                justifyContent: 'flex-end',
              }}
            >
              <Button
                id="cancel-finalized-annotation-button"
                displayLabel={formatMessage({
                  id: 'annotationContent.markNotPresentModal.cancelButton',
                })}
                variant="text"
                sx={{
                  fontSize: '15px',
                  textTransform: 'none',
                }}
                onClickHandler={() => setShowFinalizedAnnotationModal(false)}
                disabled={isFinalizing}
              />
              <Button
                id="confirm-finalized-annotation-button"
                displayLabel={formatMessage({
                  id: 'annotationContent.finalizedAnnotationModal.confirmButton',
                })}
                variant="contained"
                sx={{
                  fontSize: '15px',
                  textTransform: 'none',
                  backgroundColor: theme.palette.primary.main,
                }}
                onClickHandler={handleFinalize}
                disabled={isFinalizing}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};
